/**
 * O registry PARCIAL do Consolidador — spec-009, [T010].
 *
 * O que a tela de carteira já desenha (posição consolidada, alocação por
 * classe, movimentações, contas e rentabilidade) passa a ser desenhável DENTRO
 * da conversa, sem que o `chat-message.vue` saiba que o consolidador existe:
 * ele resolve a chave que o backend mandou (`ui.component`) no `UI_REGISTRY` e
 * monta. Cartão novo do módulo é uma entrada aqui — nunca uma linha na bolha.
 *
 * ## Como cada peça se encaixa
 *
 * | peça | onde mora | por quê |
 * |---|---|---|
 * | `component` | `ia/cartoes/*.js` | embrulho que traduz o `ui.props` em props |
 * | `schema` | `ui-registry/schemas/` | CÓPIA byte a byte do `contratos/` do backend |
 * | helpers de unidade | `ia/adaptadores.js` | moeda/data/percentual, compartilhados |
 * | ação de abrir | `ia/acoes.js` ([T019]) | rota e contexto do cliente se resolvem lá |
 *
 * ★ O `component` NÃO aponta para o `.vue`. Aponta para o embrulho, porque o
 * fio é snake_case (`pl_atual`, `saldo_pct`, `total_contas`) e os componentes
 * reusados da tela falam o vocabulário deles. Apontar direto deixaria o cartão
 * montado e VAZIO, que é a falha calada que este contrato existe para evitar.
 *
 * ## Três decisões que valem explicar
 *
 * 1. **A flag é a do MÓDULO (`FEATURES.ASSET_LOCATION`).** É ela que gateia a
 *    rota do consolidador (`constants/navigation-routes.js:276` e `:282`), que
 *    é a tela onde esses mesmos números aparecem. O registry é fail-closed na
 *    flag: escritório sem o módulo vê `fallback-card` com `flag_desligada`, em
 *    vez de um dado que ele não contratou.
 * 2. **`states` são os dois de leitura.** `input-available` entra porque é o
 *    estado em que a parte vive enquanto a tool roda: recusá-lo faria aparecer
 *    um cartão de ERRO (`estado_nao_suportado`) no meio de todo turno normal.
 *    Os desfechos de erro ficam de fora — nenhum destes cartões sabe desenhar
 *    uma falha, e o `fallback-card` diz o que aconteceu.
 * 3. **Nada entra no `LEGACY_TOOL_TO_UI`.** As tools declaram `ui` desde a wave
 *    1, então a parte já chega com a chave. Mapear os nomes no mapa legado faria
 *    as mensagens ANTIGAS (persistidas sem `ui`) resolverem para estes cartões
 *    com o `value` da tool no lugar do `ui.props` — outro shape — e a conversa
 *    reaberta trocaria o silêncio de hoje por um `fallback-card` de contrato.
 *
 * ## O `label` é o do BACKEND
 *
 * `label` é o que o `tool-call-badge` mostra ENQUANTO a tool roda, e por isso é
 * byte a byte o `LABEL` da tool emissora. Cada uma das cinco chaves tem UMA tool
 * só (diferente de `financial-planning/link`, que tem duas e por isso carrega o
 * prefixo comum), e o `registry.unit.js` compara cada rótulo com o `.py`.
 *
 * ⚠️ Duas das cinco tools declaram `LABEL = LABEL_DA_TOOL`, uma constante de
 * módulo, em vez do literal. O teste resolve a indireção no próprio arquivo em
 * vez de redigitar a string — um espelho manual voltaria a divergir na primeira
 * troca de rótulo, e o badge anunciaria uma coisa enquanto a tool faz outra.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { FEATURES } from '@constants/Text'
import { ROUTE_KEYS } from '@constants/navigation-routes'

/** A chave do módulo no catálogo de navegação. */
const MODULO = ROUTE_KEYS.CONSOLIDADOR_MINHA

/** A flag do módulo — ver a decisão 1 no cabeçalho. */
const FLAG_DO_MODULO = FEATURES.ASSET_LOCATION

// Enquanto a tool roda, e quando o resultado chega.
const ESTADOS_DE_LEITURA = ['input-available', 'output-available']

/**
 * As chaves, no formato `<modulo>/<cartao>` do contrato.
 *
 * Exportadas porque quem lê o registry e quem escreve o teste precisam da MESMA
 * string: uma chave redigitada não quebra nada visivelmente — o cartão
 * simplesmente não aparece.
 *
 * ★ O prefixo é `carteira/`, e não `consolidator/`: a chave é o VOCABULÁRIO do
 * produto (é o que o modelo lê e o que o backend emite), não o nome da pasta do
 * front. O cartão de cliente (`cliente/resumo`) mora noutro parcial pela mesma
 * razão — módulo dono, não prefixo.
 */
export const CHAVES = {
  POSICAO: 'carteira/posicao-consolidada',
  ALOCACAO: 'carteira/alocacao-por-classe',
  MOVIMENTACOES: 'carteira/movimentacoes',
  CONTAS: 'carteira/contas',
  RENTABILIDADE: 'carteira/rentabilidade',
}

export default {
  [CHAVES.POSICAO]: {
    component: () =>
      import('@modules/consolidator/ia/cartoes/posicao-consolidada'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-carteira-posicao-consolidada.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando ativos...',
  },
  [CHAVES.ALOCACAO]: {
    component: () =>
      import('@modules/consolidator/ia/cartoes/alocacao-por-classe'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-carteira-alocacao-por-classe.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando alocação da carteira...',
  },
  [CHAVES.MOVIMENTACOES]: {
    component: () => import('@modules/consolidator/ia/cartoes/movimentacoes'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-carteira-movimentacoes.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando movimentações...',
  },
  [CHAVES.CONTAS]: {
    component: () => import('@modules/consolidator/ia/cartoes/contas'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-carteira-contas.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando contas bancárias...',
  },
  [CHAVES.RENTABILIDADE]: {
    component: () => import('@modules/consolidator/ia/cartoes/rentabilidade'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-carteira-rentabilidade.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando rentabilidade...',
  },
}
